<script lang="ts" setup>
    /**
     * This component is built in a way to only run on the client. This
     * decision was made due to our CloudFront setup not only caching assets
     * but also the server response. This would lead to issues similar to cache
     * poisoning.
     */

    import { useTemplateRef } from 'vue';
    import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
    import { useConfig } from '~/composables/storyblok-proxy/useConfig';

    const COOKIE_NAME = 'redirect_modal_shown';
    const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10; // Google Chrome limits cookies to 400 days (https://developer.chrome.com/blog/cookie-max-age-expires/)

    const modalContent = useState('redirect_modal_content', () => null);
    const modalButtonText = useState('redirect_modal_button_text', () => null);

    const target = useTemplateRef<HTMLDivElement>('target');
    const { activate, deactivate } = useFocusTrap(target);

    const modalController = useModal();

    onMounted(async () => {
        // Check if we've gotten redirected via query params
        const route = useRoute();
        const utmSource = String(route.query.utm_source || '');
        const utmMedium = String(route.query.utm_medium || '');
        if (utmSource !== 'medice' || utmMedium !== 'redirect') return;

        // Check if modal has been shown already
        const cookie = useCookie(COOKIE_NAME, {
            maxAge: COOKIE_MAX_AGE
        });
        if (cookie.value) return;

        // Fetch modal content from Storyblok
        const { data: story } = await useConfig();
        if (!story.value?.content.redirectModalContent?.[0]) return;

        modalContent.value = story.value.content.redirectModalContent[0];
        modalButtonText.value = story.value.content.redirectModalButtonText;

        // Show modal and set cookie
        modalController.open();
        cookie.value = new Date().toISOString();
    });

    const handleClose = () => {
        modalController.close();
    };

    watch(modalController.isOpen, (isOpen) => {
        if (isOpen) {
            /**
             * Opening the modal isn't instantaneous, so we need to wait for a moment
             * to ensure the focus trap is activated after the modal is fully opened.
             */
            requestAnimationFrame(() => {
                activate();
            });
        } else {
            deactivate();
        }
    });
</script>

<template>
    <SharedModal
        :controller="modalController"
        modal-id="redirect_modal"
    >
        <div ref="target">
            <ATextComponent :blok="modalContent" />

            <div
                v-if="modalButtonText"
                class="buttonContainer"
            >
                <PrimaryButton
                    :native-button="true"
                    :button-text="modalButtonText"
                    size="medium"
                    @click="handleClose"
                />
            </div>
        </div>
    </SharedModal>
</template>

<style lang="scss" scoped>
    .buttonContainer {
        display: flex;
        justify-content: end;
        margin-top: $spacing;
    }
</style>
